import { Button } from "@/components/Button";
import { H3, P } from "@/components/Typography";
import { useTracking } from "@/requests/hooks/app";
import { useGetShopifyIntegration } from "@/requests/hooks/collect";
import { SHOPIFY_APP_SETTINGS_URL } from "@/utils/const";
import { IFeatureFlagKeys } from "@/utils/featureFlags";
import { faExternalLink } from "@fortawesome/pro-duotone-svg-icons";
import { useFlags } from "flagsmith/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const ShopifyApp = () => {
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();
  const { data: shopifyDetailsData, isLoading: isShopifyDetailsLoading } =
    useGetShopifyIntegration();
  const flags = useFlags<IFeatureFlagKeys>([
    "unlisted-public-shopify-app-install-url",
  ]);
  const appInstallUrl =
    flags?.["unlisted-public-shopify-app-install-url"]?.value;

  useEffect(() => {
    sendTracking("control-panel.collect.integration-settings.shopify-app.view");
  }, []);

  const getShopifySettingsUrl = (): string =>
    shopifyDetailsData?.appSettingsUrl || SHOPIFY_APP_SETTINGS_URL;

  const renderSectionWithButton = ({
    title,
    description,
    buttonTitle,
    buttonHref,
    buttonTrackingId,
    loading,
    disabled,
  }: {
    title: string;
    description: string;
    buttonTitle: string;
    buttonHref: string;
    buttonTrackingId: string;
    loading: boolean;
    disabled: boolean;
  }) => (
    <div className="sm mb-50">
      <H3 className="mb-10 font-bold">{title}</H3>
      <div className="mb-20">
        <P>{description}</P>
      </div>
      <Button
        as="a"
        variant="primary"
        icon={faExternalLink}
        iconPosition="end"
        iconClassName="h-15 w-15 text-white"
        href={buttonHref}
        target="_blank"
        trackingId={buttonTrackingId}
        loading={loading}
        disabled={disabled}
        extraClassNames="min-w-full md:min-w-[255px]">
        {buttonTitle}
      </Button>
    </div>
  );

  const reinstallSection = () =>
    renderSectionWithButton({
      title: t("settings.integration.shopifyApp.datashipsApp.title"),
      description: t(
        "settings.integration.shopifyApp.datashipsApp.description",
      ),
      buttonTitle: t("settings.integration.shopifyApp.datashipsApp.cta"),
      buttonHref: appInstallUrl as string,
      buttonTrackingId:
        "control-panel.collect.integration-settings.shopify-app.re-install",
      loading: false,
      disabled: false,
    });

  const renderAppSettingsSection = () =>
    renderSectionWithButton({
      title: t("settings.integration.shopifyApp.shopifySettings.title"),
      description: t(
        "settings.integration.shopifyApp.shopifySettings.description",
      ),
      buttonTitle: t("settings.integration.shopifyApp.shopifySettings.cta"),
      buttonHref: getShopifySettingsUrl(),
      buttonTrackingId:
        "control-panel.collect.integration-settings.shopify-app.app-settings",
      loading: isShopifyDetailsLoading,
      disabled: isShopifyDetailsLoading,
    });

  return (
    <div className="flex flex-col gap-30">
      {reinstallSection()}
      {renderAppSettingsSection()}
    </div>
  );
};

export default ShopifyApp;
